
import React, { useEffect } from 'react';
import { DocumentArrowDownIcon, PrinterIcon, XMarkIcon } from '@heroicons/react/24/outline';

const PolicyDocument = ({ onClose }) => {
  // Handle escape key press and prevent body scroll
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleDownloadPDF = () => {
    // Create a temporary element to capture the content
    const printContent = document.getElementById('policy-content');
    if (!printContent) return;
    
    const originalTitle = document.title;
    document.title = 'Leave & Attendance Policy - Aizops Pvt. Ltd.';
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to download the PDF');
      return;
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Leave & Attendance Policy - Aizops Pvt. Ltd.</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Segoe UI', Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 1200px;
            margin: 0 auto;
            padding: 40px;
            background: white;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            border-bottom: 3px solid #4f46e5;
            padding-bottom: 20px;
          }
          h1 {
            color: #1e293b;
            font-size: 28px;
            margin-bottom: 10px;
          }
          .company {
            color: #4f46e5;
            font-size: 18px;
            font-weight: 500;
          }
          h2 {
            color: #1e293b;
            font-size: 22px;
            margin-top: 30px;
            margin-bottom: 15px;
            border-left: 4px solid #4f46e5;
            padding-left: 15px;
          }
          h3 {
            color: #334155;
            font-size: 18px;
            margin-top: 20px;
            margin-bottom: 10px;
          }
          .section {
            margin-bottom: 30px;
          }
          .sub-section {
            margin-left: 20px;
            margin-bottom: 20px;
          }
          ul, ol {
            margin-left: 20px;
            margin-top: 10px;
          }
          li {
            margin-bottom: 8px;
          }
          .highlight {
            background: #fef3c7;
            padding: 15px;
            border-radius: 8px;
            margin: 15px 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }
          th {
            background: #f1f5f9;
            font-weight: 600;
          }
          .footer {
            margin-top: 50px;
            text-align: center;
            font-size: 12px;
            color: #64748b;
            border-top: 1px solid #e2e8f0;
            padding-top: 20px;
          }
          @media print {
            body {
              padding: 20px;
            }
            .no-print {
              display: none;
            }
          }
          .bg-amber-50 { background: #fffbeb; }
          .bg-pink-50 { background: #fdf2f8; }
          .bg-blue-50 { background: #eff6ff; }
          .bg-red-50 { background: #fef2f2; }
          .bg-gray-50 { background: #f9fafb; }
          .border-l-4 { border-left-width: 4px; }
          .border-amber-500 { border-color: #f59e0b; }
          .border-pink-600 { border-color: #db2777; }
          .border-blue-600 { border-color: #2563eb; }
          .border-red-500 { border-color: #ef4444; }
          .rounded-lg { border-radius: 0.5rem; }
          .p-4 { padding: 1rem; }
          .p-6 { padding: 1.5rem; }
          .mt-2 { margin-top: 0.5rem; }
          .mt-4 { margin-top: 1rem; }
          .mb-2 { margin-bottom: 0.5rem; }
          .mb-4 { margin-bottom: 1rem; }
          .mb-8 { margin-bottom: 2rem; }
          .text-sm { font-size: 0.875rem; }
          .text-lg { font-size: 1.125rem; }
          .text-xl { font-size: 1.25rem; }
          .font-semibold { font-weight: 600; }
          .font-bold { font-weight: 700; }
          .text-gray-700 { color: #374151; }
          .text-gray-800 { color: #1f2937; }
          .text-indigo-600 { color: #4f46e5; }
          .text-amber-800 { color: #92400e; }
          .text-red-600 { color: #dc2626; }
          .list-disc { list-style-type: disc; }
          .list-inside { list-style-position: inside; }
          .space-y-2 > * + * { margin-top: 0.5rem; }
          .ml-4 { margin-left: 1rem; }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    document.title = originalTitle;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClose = (e) => {
    // Prevent closing if clicking on the modal content
    if (e && e.target === e.currentTarget) {
      if (onClose) {
        onClose();
      }
    } else if (!e) {
      if (onClose) {
        onClose();
      }
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="min-h-screen px-4 py-6">
        <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-2xl">
          {/* Header with buttons */}
          <div className="sticky top-0 bg-white border-b border-gray-200 rounded-t-xl px-6 py-4 flex justify-between items-center z-10">
            <h2 className="text-xl font-bold text-gray-800">Leave & Attendance Policy</h2>
            <div className="flex gap-2">
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                aria-label="Download PDF"
              >
                <DocumentArrowDownIcon className="w-4 h-4" />
                Download PDF
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                aria-label="Print"
              >
                <PrinterIcon className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={() => typeof onClose === 'function' && onClose()}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Policy Content */}
          <div id="policy-content" className="p-6 md:p-8 max-h-[calc(100vh-120px)] overflow-y-auto">
            {/* Header */}
            <div className="text-center mb-8 pb-6 border-b-2 border-indigo-600">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                Leave & Attendance Policy
              </h1>
              <p className="text-lg text-indigo-600 font-semibold">Aizops Pvt. Ltd.</p>
              <p className="text-sm text-gray-500 mt-2">Effective Date: January 1, 2024</p>
              <p className="text-sm text-gray-500">Version: 2.0 | Last Updated: {new Date().toLocaleDateString()}</p>
            </div>

            {/* 1. Objective */}
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 border-l-4 border-indigo-600 pl-4">
                1. Objective
              </h2>
              <p className="text-gray-700 leading-relaxed">
                To promote work-life balance for all employees of Aizops Pvt. Ltd. while ensuring operational efficiency and fair treatment across the organization.
              </p>
            </div>

            {/* 2. Eligibility */}
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 border-l-4 border-indigo-600 pl-4">
                2. Eligibility
              </h2>
              <p className="text-gray-700 leading-relaxed">
                This policy applies to all permanent employees of Aizops Pvt. Ltd. Contract employees, interns, and temporary staff are covered under separate policies as defined in their appointment letters.
              </p>
            </div>

            {/* 3. General Provisions */}
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 border-l-4 border-indigo-600 pl-4">
                3. General Provisions
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">3.1 Leave Types (Permanent Employees)</h3>
              <p className="text-gray-700 mb-2">Eligible employees may avail the following leave types:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li><span className="font-semibold">Privileged Leaves (PL):</span> 12 days per annum</li>
                <li><span className="font-semibold">Sick Leave (SL):</span> 10 days per annum</li>
                <li><span className="font-semibold">Maternity Leave:</span> As per the Maternity Benefits Act, 1961 (as amended)</li>
                <li><span className="font-semibold">Paternity Leave:</span> 7 days (for male employees)</li>
                <li><span className="font-semibold">Bereavement Leave:</span> 3 days (in case of immediate family member demise)</li>
                <li><span className="font-semibold">Leave Without Pay (LWP)</span></li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">3.2 Leave Year</h3>
              <p className="text-gray-700">The leave year follows the calendar year: <span className="font-semibold">January 1 to December 31</span></p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">3.3 Leave Application & Approval</h3>
              <p className="text-gray-700">All leave requests must be submitted and approved via HRMS only. Applications through email or other channels will not be considered valid.</p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">3.4 Pro-rata Credit for Mid-year Joiners</h3>
              <p className="text-gray-700">
                Employees joining during the year (excluding maternity & paternity leave) receive leave credited on a pro-rata basis 
                at the time of joining. The next full leave credit occurs only in the following calendar year.
              </p>
            </div>

            {/* 4. Privileged Leave */}
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 border-l-4 border-indigo-600 pl-4">
                4. Privileged Leave (PL)
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>12 days of paid privileged leave per year, credited at the start of each leave year</li>
                <li>Any holidays, non-working Saturdays, or Sundays falling during the leave period are not counted as part of the leave</li>
                <li><span className="font-semibold">Carry forward limit:</span> Maximum 10 days can be carried forward to the next year as of December 31</li>
                <li><span className="font-semibold">Accumulation cap:</span> Total allowable PL balance shall not exceed 25 days</li>
                <li>Any unavailed leave above 25 days as of December 31 shall lapse</li>
                <li>During probation: Employees are generally not expected to avail PL (except for personal exigencies), but leave continues to accrue</li>
                <li>Prior approval from the respective Functional Head is mandatory</li>
                <li>A positive leave balance does not automatically entitle leave approval; sanction depends on work exigencies</li>
                <li>The company reserves the right to decline leave dates/periods if they conflict with business needs</li>
                <li>Leave applications must be submitted at least one week in advance</li>
              </ul>
              
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-4 rounded-lg">
                <p className="font-semibold text-amber-800">⚠️ At Exit:</p>
                <p className="text-gray-700 text-sm mt-1">
                  If an employee has taken more PL than their pro-rata entitlement, the excess will be deducted from their final settlement. 
                  The Head of HR / CEO may waive this recovery at their discretion.
                </p>
              </div>
            </div>

            {/* 5. Sick Leave / Casual Leave */}
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 border-l-4 border-indigo-600 pl-4">
                5. Sick Leave / Casual Leave (SL/CL)
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>10 days of sick/casual leave per year, credited at the start of the leave year</li>
                <li><span className="font-semibold">No carry forward:</span> Unavailed SL/CL lapses at the end of each leave year</li>
                <li>Any sickness/illness exceeding 10 days must be covered from the employee's PL balance</li>
                <li>Medical certificate required for sick leave of more than 2 consecutive days</li>
                <li>Casual leave requires prior approval from the Functional Head, with one day's notice</li>
              </ul>
            </div>

            {/* 6. Leave Without Pay */}
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 border-l-4 border-indigo-600 pl-4">
                6. Leave Without Pay (LWP)
              </h2>
              <p className="text-gray-700 mb-2">Maximum 60 days in a calendar year:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li><span className="font-semibold">Medical reasons:</span> After exhausting all SL and PL entitlements – up to 60 days</li>
                <li><span className="font-semibold">Maternity/Paternity extension:</span> After exhausting maternity/paternity leave entitlement</li>
                <li>LWP is reported as a 7-day week (including Saturdays and Sundays)</li>
                <li>Requires approval from Functional Head, MD & CEO</li>
              </ul>
            </div>

            {/* 7. Maternity Leave */}
            <div className="mb-8 bg-pink-50 rounded-lg p-6">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 border-l-4 border-pink-600 pl-4">
                7. Maternity Leave
              </h2>
              <p className="text-gray-700 font-semibold mb-2">As per the Maternity Benefits Act, 1961 (as amended):</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li><span className="font-semibold">26 weeks</span> of paid leave for female employees with fewer than two surviving children</li>
                <li><span className="font-semibold">12 weeks</span> of paid leave for female employees with two or more surviving children</li>
                <li>Eligibility condition: Must have worked at least 80 days in the 12 months preceding expected delivery</li>
                <li>Adoption / commissioning mother: 12 weeks of maternity benefit</li>
              </ul>
            </div>

            {/* 8. Paternity Leave */}
            <div className="mb-8 bg-blue-50 rounded-lg p-6">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 border-l-4 border-blue-600 pl-4">
                8. Paternity Leave
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>7 working days of paid paternity leave for male employees at the time of childbirth</li>
                <li>Leave may be taken within 3 months from the date of birth</li>
                <li>Restricted to up to 2 child births</li>
                <li>Cannot be carried forward</li>
              </ul>
            </div>

            {/* Attendance Policy */}
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 border-l-4 border-indigo-600 pl-4">
                Attendance Policy – Aizops Pvt. Ltd.
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">1. Office Timings</h3>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-gray-700"><span className="font-semibold">Monday to Saturday:</span> 09:30 AM – 06:30 PM</p>
                <p className="text-gray-700"><span className="font-semibold">Lunch Break:</span> 01:00 PM – 02:00 PM</p>
                <p className="text-gray-700 mt-2"><span className="font-semibold">Total Working Hours:</span> 8 hours per day (excluding lunch break)</p>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">1.1 Grace Period</h3>
              <p className="text-gray-700 mb-2">
                A grace period of <span className="font-semibold">15 minutes (9:30 AM to 9:45 AM)</span> is allowed for only <span className="font-semibold">three days in a month</span>.
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mt-2 rounded-lg">
                <p className="text-gray-700">
                  ⚠️ Once the three grace days are exhausted, any further late arrival (after 9:30 AM) within the same month will result in 
                  <span className="font-semibold text-red-600"> half-day absent</span> marked, with corresponding salary deduction.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">1.2 Arrival after 9:45 AM</h3>
              <p className="text-gray-700">No grace period benefit for that day. Half-day working will be recorded, with corresponding salary deduction.</p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">2. Unauthorized Absence</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Any absence exceeding accrued leave entitlement is treated as unauthorized absence</li>
                <li>Consecutive unauthorized absence of more than 2 days may lead to disciplinary action</li>
                <li>10 non-consecutive days in a year may lead to termination</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">3. Work From Home (WFH)</h3>
              <p className="text-gray-700">WFH may be granted at the discretion of the reporting manager based on business requirements and nature of work.</p>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} Aizops Pvt. Ltd. All rights reserved.
              </p>
              <p className="text-xs text-gray-400 mt-2">
                This document is confidential and proprietary to Aizops Pvt. Ltd.
              </p>
              <p className="text-xs text-gray-400 mt-1">
                For any queries, please contact HR department at hr@aizops.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyDocument;